/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Loader2,
  RotateCcw,
  ExternalLink,
  Instagram,
  CheckCircle2,
  XCircle,
  Clock,
  EyeOff,
  History,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OUTCOME_META = {
  approved:           { label: 'Approved',  className: 'border-emerald-700 text-emerald-300', Icon: CheckCircle2 },
  rejected:           { label: 'Rejected',  className: 'border-red-800 text-red-300',         Icon: XCircle },
  skipped_past:       { label: 'Past',      className: 'border-amber-800 text-amber-300',     Icon: Clock },
  skipped_off_topic:  { label: 'Off-topic', className: 'border-zinc-700 text-zinc-300',       Icon: EyeOff },
};

const prettyTime = (iso) => {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return iso.slice(0, 10);
    return format(d, 'd MMM yyyy, HH:mm');
  } catch {
    return iso.slice(0, 16);
  }
};

const AdminScrapeHistoryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [busyUrl, setBusyUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/instagram/scrape-history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const requeue = async (post_url) => {
    if (!window.confirm('Re-queue this post? It will be re-processed on the next scrape.')) return;
    setBusyUrl(post_url);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(
        `${API}/admin/instagram/scrape-history/forget`,
        { post_url },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Forgotten — next scrape will pick it up again.');
      setItems((p) => p.filter((i) => i.post_url !== post_url));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not forget');
    } finally {
      setBusyUrl(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="admin-scrape-history-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-12">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4" data-testid="history-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <History className="w-6 h-6 text-accent" />
            Scrape History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Posts we've already decided on. Tap <span className="text-foreground">Re-queue</span> to make a post eligible for the next scrape again.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-card/60 border-border/40">
            <CardContent className="p-10 text-center space-y-2">
              <p className="font-medium">No history yet.</p>
              <p className="text-sm text-muted-foreground">
                Run a scrape and decisions will show here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="history-list">
            {items.map((h) => {
              const meta = OUTCOME_META[h.outcome] || { label: h.outcome, className: 'border-border', Icon: History };
              const Icon = meta.Icon;
              return (
                <Card key={h.post_url} className="bg-card border-border/40">
                  <CardContent className="p-3 flex items-center gap-3">
                    <Badge variant="outline" className={`text-xs ${meta.className} flex-shrink-0`}>
                      <Icon className="w-3 h-3 mr-1" />
                      {meta.label}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <a
                        href={h.post_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm hover:text-accent transition flex items-center gap-1 truncate"
                      >
                        <Instagram className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">@{h.ig_username || '?'}</span>
                        <ExternalLink className="w-3 h-3 opacity-60 flex-shrink-0" />
                      </a>
                      <p className="text-xs text-muted-foreground">
                        {prettyTime(h.decided_at)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => requeue(h.post_url)}
                      disabled={busyUrl === h.post_url}
                      data-testid={`requeue-${(h.post_url || '').split('/').pop()}`}
                      className="flex-shrink-0"
                    >
                      {busyUrl === h.post_url ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-3.5 h-3.5 mr-1" />
                          Re-queue
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminScrapeHistoryPage;
